import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'
import { SECTION_TITLE_TYPE } from '../typography/typography'

/* Family 6 · Cards and lists. Born because a page was writing `rounded-md border p-3`
   by hand: radius, border and surface are the definition of "card". */

/* `accent` and `ink` open a colour island for their subtree (`theme.css`): a Button inside the
   lime card turns black unasked. Variants, not defaults: every card lime, and none stands out. */
/* `sunken` reads as a well cut into the page rather than a card lifted off it: darker than
   the page, and no shadow, because a shadow is what says «above». */
export const cardVariants = cva('', {
  variants: {
    variant: {
      default: 'bg-card text-card-foreground shadow-card rounded-lg border border-border',
      accent: 'bg-card text-card-foreground shadow-card rounded-lg',
      ink: 'bg-background text-foreground rounded-lg',
      sunken: 'bg-surface-sunken text-surface-sunken-foreground rounded-lg',
    },
  },
  defaultVariants: { variant: 'default' },
})

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

const SURFACE_OF: Partial<Record<NonNullable<CardProps['variant']>, 'ink' | 'lime'>> = {
  ink: 'ink',
  accent: 'lime',
}

/** The `data-surface` a variant opens, for whatever element wears `cardVariants` by hand. */
export function surfaceOf(variant: CardProps['variant']): 'ink' | 'lime' | undefined {
  return variant == null ? undefined : SURFACE_OF[variant]
}

/** The container surface: border, radius and shadow from the design system. */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      data-surface={surfaceOf(variant)}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  ),
)
Card.displayName = 'Card'

export type CardHeaderProps = React.HTMLAttributes<HTMLDivElement>

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 p-4', className)} {...props} />
  ),
)
CardHeader.displayName = 'CardHeader'

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Picks the heading level that fits the page hierarchy. */
  asChild?: boolean
}

/** A card's heading: the section-title type without the slash, which marks page sections. */
export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'h2'
    return <Component ref={ref} className={cn(SECTION_TITLE_TYPE, className)} {...props} />
  },
)
CardTitle.displayName = 'CardTitle'

export type CardContentProps = React.HTMLAttributes<HTMLDivElement>

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    // `pt-0` because the content normally follows a CardHeader, which already has its
    // own bottom padding: without it the gap doubles.
    <div ref={ref} className={cn('p-4 pt-0', className)} {...props} />
  ),
)
CardContent.displayName = 'CardContent'

/* `separated` draws the rule a footer of secondary actions needs when nothing above it
   in the card (no CardHeader, or content that reads as its own block) already implies
   one — a compact status card whose actions come after a hairline, not a header. */
export const cardFooterVariants = cva('flex flex-wrap items-center gap-2 p-4', {
  variants: {
    variant: {
      default: 'pt-0',
      separated: 'border-t border-border pt-3',
    },
  },
  defaultVariants: { variant: 'default' },
})

export interface CardFooterProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardFooterVariants> {}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardFooterVariants({ variant }), className)} {...props} />
  ),
)
CardFooter.displayName = 'CardFooter'
